/**
 * Helper function to calculate available questions based on selected difficulty levels
 */
const getAvailableQuestions = (levels: number[], category: any): number => {
	let total = 0;
	if (levels.includes(1)) total += category.available_questions_1 || 0;
	if (levels.includes(2)) total += category.available_questions_2 || 0;
	if (levels.includes(3)) total += category.available_questions_3 || 0;
	return total;
};

/**
 * Calculates available questions for a category by summing available questions
 * from all subcategories for the selected levels
 */
const getCategoryAvailableQuestionsFromSubcategories = (
	levels: number[],
	category: any
): number => {
	if (!category.subcategories || category.subcategories.length === 0) {
		// If no subcategories, fall back to category's own available_questions
		return getAvailableQuestions(levels, category);
	}

	// Sum available questions from all subcategories for the selected levels
	return category.subcategories.reduce((total: number, subcategory: any) => {
		const subAvailable = getAvailableQuestions(levels, subcategory);
		return total + subAvailable;
	}, 0);
};

/**
 * Checks the maximum number of available questions for a category or subcategory
 * based on selected difficulty levels
 */

const difficultyLevels: number[] = [1, 2, 3];
const MAX_QUESTIONS = 50;

export const checkAvailableQuestions = (
	item: any,
	max: number = MAX_QUESTIONS
): number => {
	const levels = item.levels || [];
	let availableQuestions: number;

	if (levels.length > 0) {
		// Check if this is a category (has subcategories)
		if (item.subcategories && item.subcategories.length > 0) {
			// For categories, sum from subcategories
			availableQuestions = getCategoryAvailableQuestionsFromSubcategories(
				levels,
				item
			);
		} else {
			// For subcategories, use their own available_questions
			availableQuestions = getAvailableQuestions(levels, item);
		}
	} else {
		// If no levels are selected, pretend all levels are selected
		const allLevels = difficultyLevels;
		if (item.subcategories && item.subcategories.length > 0) {
			// For categories, sum from subcategories
			availableQuestions = getCategoryAvailableQuestionsFromSubcategories(
				allLevels,
				item
			);
		} else {
			// For subcategories, use their own available_questions
			availableQuestions = getAvailableQuestions(allLevels, item);
		}
	}

	return Math.min(availableQuestions, max);
};

/**
 * Validates the question count against available questions
 */
export const validateQuestionCount = (
	category: any,
	questionCount: number | null,
	max: number = MAX_QUESTIONS
): { available: number; invalid: boolean } => {
	const available = checkAvailableQuestions(category, max);
	const invalid = (questionCount ?? 0) > available;
	return { available, invalid };
};
